import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import "../styles/AuthScreen.css";

/**
 * ✅ IMPORTANT:
 * - REACT_APP_API_URL should be like: https://pay2pee.onrender.com
 * - NOT: https://pay2pee.onrender.com/api
 */
const API_ORIGIN = (() => {
  const clean = (v) =>
    (v || "")
      .trim()
      .replace(/\/+$/, "")
      .replace(/\/api$/, "");

  const env = clean(process.env.REACT_APP_API_URL);
  if (env) return env;

  if (typeof window === "undefined") return "";

  const origin = window.location.origin || "";
  const isCapacitor = origin.startsWith("capacitor://");
  const isLocalhost = /localhost|127\.0\.0\.1/.test(origin);

  if (isCapacitor || isLocalhost) return "https://pay2pee.onrender.com";
  return "https://pay2pee.onrender.com";
})();

const API_BASE = API_ORIGIN ? `${API_ORIGIN}/api` : "/api";

/* =========================
   ✅ GA4 EVENT HELPER
   ========================= */
const gaEvent = (name, params = {}) => {
  try {
    if (typeof window === "undefined") return;
    if (typeof window.gtag !== "function") return;

    window.gtag("event", name, {
      page_path: window.location.pathname,
      ...params,
    });
  } catch {
    // no-op
  }
};

/* =========================
   ✅ FIRST-TIME WELCOME FLAG
   ========================= */
const SEEN_WELCOME_KEY = "p2p_seen_welcome";

export default function AuthScreen() {
  const navigate = useNavigate();
  const location = useLocation();

  const [activeSide, setActiveSide] = useState("user"); // "user" | "partner"
  const [isLogin, setIsLogin] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [showDebugInfo] = useState(false);

  // ✅ NEW
  const [isResetting, setIsResetting] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetMessage, setResetMessage] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    email: "",
    password: "",
    submit: "",
  });

  const getNextRoute = () => {
    try {
      const params = new URLSearchParams(location.search || "");
      const next = params.get("next");

      if (!next) return null;

      const decoded = decodeURIComponent(next);

      if (decoded.startsWith("#/")) {
        return decoded.replace(/^#/, "");
      }

      if (decoded.startsWith("/")) {
        return decoded;
      }

      return null;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    try {
      if (typeof window === "undefined") return;

      const params = new URLSearchParams(location.search || "");
      const fromWelcome = params.get("from") === "welcome";

      if (fromWelcome) {
        localStorage.setItem(SEEN_WELCOME_KEY, "1");
      }
    } catch {
      // no-op
    }
  }, [location.search]);

  useEffect(() => {
    try {
      if (typeof window === "undefined") return;

      const host = window.location.hostname || "";
      const isPay2PeeDomain = host === "pay2pee.app" || host === "www.pay2pee.app";

      const token = localStorage.getItem("token");

      const params = new URLSearchParams(location.search || "");
      const fromWelcome = params.get("from") === "welcome";

      const seenWelcome = localStorage.getItem(SEEN_WELCOME_KEY) === "1";

      if (isPay2PeeDomain && !token && !seenWelcome && !fromWelcome) {
        navigate("/welcome", { replace: true });
      }
    } catch {
      // no-op
    }
  }, [navigate, location.search]);

  useEffect(() => {
    gaEvent("auth_view", {
      mode: isLogin ? "login" : "signup",
      role: activeSide === "partner" ? "partner" : "guest",
    });
  }, [isLogin, activeSide]);

  const handleSideSwitch = (side) => {
    if (isLoading) return;

    gaEvent("auth_role_switch", {
      to: side === "partner" ? "partner" : "guest",
      mode: isLogin ? "login" : "signup",
    });

    setActiveSide(side);
    setErrors({ name: "", email: "", password: "", submit: "" });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "", submit: "" }));
  };

  const validate = () => {
    const newErrors = { name: "", email: "", password: "", submit: "" };

    if (!isLogin && !formData.name.trim()) {
      newErrors.name =
        activeSide === "partner"
          ? "Business name is required."
          : "Your name is required.";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required.";
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email.";
    }

    if (!formData.password.trim()) {
      newErrors.password = "Password is required.";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters.";
    }

    return newErrors;
  };

  // ✅ NEW
  const handleGuestContinue = () => {
    if (isLoading) return;

    gaEvent("guest_continue_click");

    localStorage.setItem(SEEN_WELCOME_KEY, "1");
    localStorage.setItem("userType", "guest");

    navigate("/home", { replace: true });
  };

  // ✅ NEW
  const handleForgotPassword = async () => {
    if (!resetEmail.trim()) {
      setResetMessage("Please enter your email.");
      return;
    }

    try {
      setResetMessage("Sending reset link...");
      await axios.post(`${API_BASE}/auth/forgot-password`, {
        email: resetEmail.trim(),
      });
      setResetMessage("Reset link sent to your email.");
    } catch (err) {
      console.error("Forgot password error", err);
      setResetMessage(
        err?.response?.data?.message || "Something went wrong. Try again."
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;

    const validation = validate();
    const hasErrors = Object.values(validation).some((v) => v);

    if (hasErrors) {
      gaEvent("auth_submit_invalid", {
        mode: isLogin ? "login" : "signup",
        role: activeSide === "partner" ? "partner" : "guest",
      });

      setErrors(validation);
      return;
    }

    setIsLoading(true);
    setErrors((prev) => ({ ...prev, submit: "" }));

    gaEvent("auth_submit", {
      mode: isLogin ? "login" : "signup",
      role: activeSide === "partner" ? "partner" : "guest",
    });

    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/register";

      const res = await axios.post(`${API_BASE}${endpoint}`, {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        userType: activeSide === "partner" ? "partner" : "user",
      });

      const data = res.data || {};

      if (data.token) localStorage.setItem("token", data.token);
      if (data.userId || data.user_id || data.id) {
        localStorage.setItem("userId", data.userId || data.user_id || data.id);
      }
      localStorage.setItem(
        "userType",
        activeSide === "partner" ? "partner" : "guest"
      );
      if (data.name || data.userName) {
        localStorage.setItem("userName", data.name || data.userName);
      }
      if (data.avatarUrl) localStorage.setItem("avatarUrl", data.avatarUrl);

      localStorage.setItem(SEEN_WELCOME_KEY, "1");

      gaEvent(isLogin ? "login_success" : "signup_success", {
        role: activeSide === "partner" ? "partner" : "guest",
      });

      const nextRoute = getNextRoute();
      if (activeSide === "partner") {
        navigate("/partner", { replace: true });
      } else if (nextRoute) {
        navigate(nextRoute, { replace: true });
      } else {
        navigate("/home", { replace: true });
      }
    } catch (err) {
      console.error("Auth error", err);

      gaEvent(isLogin ? "login_error" : "signup_error", {
        role: activeSide === "partner" ? "partner" : "guest",
        http_status: err?.response?.status || 0,
      });

      let message = "Something went wrong. Please try again.";
      if (err.response && err.response.data && err.response.data.message) {
        message = err.response.data.message;
      }
      setErrors((prev) => ({ ...prev, submit: message }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <img
          src="/images/logo.png"
          alt="Pay2Pee Logo"
          className={`auth-logo ${
            activeSide === "partner" ? "partner-logo" : "guest-logo"
          }`}
        />

        <h1 className="auth-title">{isLogin ? "Welcome Back" : "Join Us"}</h1>

        {/* ✅ NEW */}
        <button
          type="button"
          className="guest-continue-button"
          onClick={handleGuestContinue}
          disabled={isLoading}
        >
          Continue as Guest
        </button>

        <div className="toggle-container">
          <button
            type="button"
            className={`toggle-button ${activeSide === "user" ? "active" : ""}`}
            onClick={() => handleSideSwitch("user")}
            disabled={isLoading}
          >
            Guest
          </button>
          <button
            type="button"
            className={`toggle-button ${
              activeSide === "partner" ? "active" : ""
            }`}
            onClick={() => handleSideSwitch("partner")}
            disabled={isLoading}
          >
            Partner
          </button>
        </div>

        <form className="auth-form" onSubmit={handleSubmit}>
          <h2 className="form-title">
            {isLogin
              ? activeSide === "partner"
                ? "Partner Sign In"
                : "Guest Sign In"
              : activeSide === "partner"
              ? "Create Partner Account"
              : "Create Guest Account"}
          </h2>

          {!isLogin && (
            <div className="input-group">
              <label htmlFor="name">
                {activeSide === "partner" ? "Business name" : "Your name"}
              </label>
              <input
                type="text"
                id="name"
                name="name"
                className={`auth-input ${errors.name ? "error" : ""}`}
                value={formData.name}
                onChange={handleInputChange}
                disabled={isLoading}
              />
              {errors.name && (
                <span className="error-message">{errors.name}</span>
              )}
            </div>
          )}

          <div className="input-group">
            <label htmlFor="email">Email address</label>
            <input
              type="email"
              id="email"
              name="email"
              className={`auth-input ${errors.email ? "error" : ""}`}
              value={formData.email}
              onChange={handleInputChange}
              disabled={isLoading}
            />
            {errors.email && (
              <span className="error-message">{errors.email}</span>
            )}
          </div>

          <div className="input-group">
            <label htmlFor="password">Password (min 8 characters)</label>
            <input
              type="password"
              id="password"
              name="password"
              className={`auth-input ${errors.password ? "error" : ""}`}
              value={formData.password}
              onChange={handleInputChange}
              disabled={isLoading}
            />
            {errors.password && (
              <span className="error-message">{errors.password}</span>
            )}
          </div>

          {/* ✅ NEW */}
          {isLogin && (
            <div className="forgot-password">
              <button
                type="button"
                className="switch-button"
                onClick={() => {
                  setIsResetting(true);
                  setResetEmail(formData.email || "");
                  setResetMessage("");
                }}
                disabled={isLoading}
              >
                Forgot password?
              </button>
            </div>
          )}

          {errors.submit && (
            <div className="submit-error">
              <span className="error-message">{errors.submit}</span>
            </div>
          )}

          <button
            type="submit"
            className={`submit-button ${
              activeSide === "partner" ? "partner" : "user"
            }`}
            disabled={isLoading}
          >
            {isLoading
              ? isLogin
                ? "Signing you in..."
                : "Creating account..."
              : isLogin
              ? "Sign In"
              : "Sign Up"}
          </button>
        </form>

        <div className="auth-footer">
          <button
            type="button"
            className="switch-button"
            onClick={() => {
              gaEvent("auth_mode_switch", {
                to: !isLogin ? "login" : "signup",
                role: activeSide === "partner" ? "partner" : "guest",
              });
              setIsLogin(!isLogin);
              setErrors({ name: "", email: "", password: "", submit: "" });
            }}
            disabled={isLoading}
          >
            {isLogin
              ? "Need an account? Create one"
              : "Already have an account? Sign in"}
          </button>
        </div>

        {/* ✅ NEW */}
        {isResetting && (
          <div className="reset-modal">
            <div className="reset-box">
              <h3>Reset Password</h3>

              <input
                type="email"
                className="auth-input"
                placeholder="Enter your email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
              />

              <button
                type="button"
                className="submit-button user"
                onClick={handleForgotPassword}
              >
                Send Reset Link
              </button>

              {resetMessage ? <p>{resetMessage}</p> : null}

              <button
                type="button"
                className="switch-button"
                onClick={() => {
                  setIsResetting(false);
                  setResetMessage("");
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>

      {showDebugInfo && (
        <div className="debug-info">
          <h4>Debug</h4>
          <pre>
            {JSON.stringify(
              {
                token: localStorage.getItem("token"),
                userId: localStorage.getItem("userId"),
                userType: localStorage.getItem("userType"),
                userName: localStorage.getItem("userName"),
                avatarUrl: localStorage.getItem("avatarUrl"),
                seenWelcome: localStorage.getItem(SEEN_WELCOME_KEY),
              },
              null,
              2
            )}
          </pre>
        </div>
      )}
    </div>
  );
}